import { useRef, useState } from "react";
import { Paper, Stack, Box, Typography, IconButton } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import Images from "../../utils/Images";
import { toMoneyNumber } from "../../utils/tax";
import CustomizationModal from "../CustomizationModal";

const formatCurrency = (value) => `₹${Number(value || 0)}`;

const getAddonName = (addon) => {
    const valueName =
        addon?.label ||
        addon?.name ||
        addon?.addonName ||
        addon?.productName ||
        addon?.title;

    return valueName || addon?.addonTitle || "Addon";
};

const getCartItemDescription = (cartItem, addons) => {
    if (addons.length > 0) {
        return addons.map(getAddonName).join(", ");
    }

    return cartItem?.categoryName || cartItem?.description || "";
};

const QuantitySelector = ({ quantity, onDecrease, onIncrease }) => (
    <Stack
        direction="row"
        alignItems="center"
        onPointerDown={(event) => event.stopPropagation()}
        sx={{
            height: 36,
            borderRadius: "999px",
            backgroundColor: "#EFEFEF",
            pl: 0.25,
            pr: 0.35,
            gap: 0.15,
        }}
    >
        <IconButton
            size="small"
            onClick={onDecrease}
            aria-label="Decrease quantity"
            sx={{
                width: 30,
                height: 30,
                color: "#333333",
            }}
        >
            <RemoveIcon
                sx={{
                    fontSize: 16,
                }}
            />
        </IconButton>

        <Typography
            sx={{
                minWidth: 22,
                textAlign: "center",
                fontSize: 14,
                fontWeight: 700,
                color: "#1A1A1A",
            }}
        >
            {quantity}
        </Typography>

        <IconButton
            size="small"
            onClick={onIncrease}
            aria-label="Increase quantity"
            sx={{
                width: 30,
                height: 30,
                backgroundColor: "#04A5E2",
                color: "#FFFFFF",

                "&:hover": {
                    backgroundColor: "#333333",
                },
            }}
        >
            <AddIcon
                sx={{
                    fontSize: 16,
                }}
            />
        </IconButton>
    </Stack>
);

const CartItemCard = ({
    cartItem,
    outletData,
    onIncrease,
    onDecrease,
    onRemove,
    onApplyCustomization,
    onMessage,
}) => {
    const [isCustomizing, setIsCustomizing] = useState(false);
    const [isItemHidden, setIsItemHidden] = useState(false);
    const [anchorRect, setAnchorRect] = useState(null);
    const cardRef = useRef(null);

    const addons = Array.isArray(cartItem?.addons) ? cartItem.addons : [];

    const quantity = Math.max(Number(cartItem?.quantity || 1), 1);

    const unitPrice = Number(cartItem?.unitPrice || 0);

    const itemPrice = toMoneyNumber(cartItem?.taxable ?? unitPrice * quantity);

    const productImage = cartItem?.productImage || Images.product;

    const description = getCartItemDescription(cartItem, addons);

    const canCustomize = Boolean(
        onApplyCustomization &&
            (cartItem?.hasAddon ||
                cartItem?.hasAddons ||
                addons.length > 0),
    );

    const handleToggleCustomization = () => {
        if (!canCustomize) {
            return;
        }

        if (isCustomizing) {
            setIsCustomizing(false);
            return;
        }

        const element = cardRef.current;

        if (element) {
            const currentRect = element.getBoundingClientRect();

            if (window.innerHeight - currentRect.top < 300) {
                element.scrollIntoView({
                    block: "center",
                    behavior: "auto",
                });
            }

            const nextRect = element.getBoundingClientRect();

            setAnchorRect({
                top: nextRect.top,
                left: nextRect.left,
                width: nextRect.width,
                height: nextRect.height,
            });
        }

        setIsItemHidden(true);
        setIsCustomizing(true);
    };

    return (
        <>
            <Paper
                ref={cardRef}
                elevation={0}
                sx={{
                    px: 1.25,
                    py: 1.25,
                    borderRadius: "22px",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    visibility: isItemHidden ? "hidden" : "visible",
                }}
            >
            <Stack direction="row" alignItems="stretch" spacing={1.25}>
                    <Box
                        component="img"
                        src={productImage}
                        alt={cartItem?.productName || "Product"}
                        sx={{
                            width: {
                                xs: 72,
                                sm: 80,
                            },
                            height: {
                                xs: 72,
                                sm: 80,
                            },
                            flexShrink: 0,
                            borderRadius: "16px",
                            objectFit: "cover",
                            backgroundColor: "#F3F4F6",
                        }}
                    />

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            py: 0.15,
                        }}
                    >
                        <Box
                            sx={{
                                minWidth: 0,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: 15,
                                        sm: 16,
                                    },
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    color: "#1A1A1A",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {cartItem?.productName || "Product"}
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.6,
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: "#04A5E2",
                                }}
                            >
                                {formatCurrency(itemPrice)}
                            </Typography>

                            {(description || canCustomize) && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.15}
                                    onPointerDown={(event) => {
                                        if (!canCustomize) {
                                            return;
                                        }

                                        event.stopPropagation();
                                    }}
                                    onClick={handleToggleCustomization}
                                    role={canCustomize ? "button" : undefined}
                                    aria-expanded={
                                        canCustomize ? isCustomizing : undefined
                                    }
                                    sx={{
                                        mt: 0.35,
                                        width: "fit-content",
                                        maxWidth: "100%",
                                        cursor: canCustomize
                                            ? "pointer"
                                            : "default",
                                    }}
                                >
                                    {description && (
                                        <Typography
                                            sx={{
                                                minWidth: 0,
                                                maxWidth: canCustomize
                                                    ? "calc(100% - 20px)"
                                                    : "100%",
                                                fontSize: 12,
                                                lineHeight: 1.45,
                                                color: "#9CA3AF",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {description}
                                        </Typography>
                                    )}

                                    {canCustomize && (
                                        <KeyboardArrowDownRoundedIcon
                                            sx={{
                                                fontSize: 20,
                                                color: "#04A5E2",
                                                flexShrink: 0,
                                                transform: isCustomizing
                                                    ? "rotate(180deg)"
                                                    : "rotate(0deg)",
                                                transition:
                                                    "transform 0.25s ease",
                                            }}
                                        />
                                    )}
                                </Stack>
                            )}
                        </Box>
                    </Box>

                    <Stack
                        alignItems="flex-end"
                        justifyContent="space-between"
                        sx={{
                            flexShrink: 0,
                            minHeight: {
                                xs: 72,
                                sm: 80,
                            },
                        }}
                    >
                        <IconButton
                            size="small"
                            aria-label={`Remove ${cartItem?.productName || "item"}`}
                            onClick={() => onRemove?.()}
                            sx={{
                                width: 32,
                                height: 32,
                                color: "#E53935",

                                "&:hover": {
                                    backgroundColor: "#FFF1F1",
                                },
                            }}
                        >
                            <DeleteOutlineIcon
                                sx={{
                                    fontSize: 20,
                                }}
                            />
                        </IconButton>

                        <QuantitySelector
                            quantity={quantity}
                            onDecrease={() => onDecrease(cartItem?.cartItemId)}
                            onIncrease={() => onIncrease(cartItem?.cartItemId)}
                        />
                    </Stack>
                </Stack>
            </Paper>

            {canCustomize && (
                <CustomizationModal
                    open={isCustomizing}
                    cartItem={cartItem}
                    outletData={outletData}
                    anchorRef={cardRef}
                    initialAnchorRect={anchorRect}
                    onClose={() => setIsCustomizing(false)}
                    onExited={() => setIsItemHidden(false)}
                    onProceed={onApplyCustomization}
                    onMessage={onMessage}
                />
            )}
        </>
    );
};

export default CartItemCard;
